import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useBeforeUnload, useBlocker } from 'react-router-dom';
import NavBar from './components/nav-bar.tsx';
import WritingCursor from './components/WritingCursor.tsx';
import { sectionUrl } from './lib/postsApi.ts';
import { createPostsApiVersionClient } from './lib/postsApiVersionClient.ts';
import { useGoogleAuth } from './lib/useGoogleAuth.ts';
import { VersionHistoryPanel } from './VersionHistoryPanel.tsx';
import './styling/private-theme.css';
import './styling/write-editor.css';

export default function WriteEditor() {
  // Read at render time so tests can set process.env before rendering
  const BASE_URL = process.env.REACT_APP_POSTS_API_BASE_URL;
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const { authState, googleToken, googleBtnRef, signOut } = useGoogleAuth();
  const writeVersionClient = useMemo(
    () => createPostsApiVersionClient('writing', slug ?? '', googleToken),
    [slug, googleToken],
  );

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [body, setBody] = useState('');
  const [published, setPublished] = useState(false);
  const [loading, setLoading] = useState(!!slug);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autosaveStatus, setAutosaveStatus] = useState('');
  const [unsavedSinceApi, setUnsavedSinceApi] = useState(false);

  const localTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const latestDraft = useRef({ title, description, body, published });
  const loadedSlugRef = useRef<string | undefined>(undefined);

  // Sync latestDraft ref on every field change (no re-render cost)
  useEffect(() => {
    latestDraft.current = { title, description, body, published };
  }, [title, description, body, published]);

  // localStorage restore effect — runs once on mount
  useEffect(() => {
    if (authState !== 'authenticated') return;
    const key = slug ? `write-draft-${slug}` : 'write-new-draft';
    const raw = localStorage.getItem(key);
    if (!raw) return;
    try {
      const draft = JSON.parse(raw);
      if (draft.title !== undefined) setTitle(draft.title);
      if (draft.description !== undefined) setDescription(draft.description);
      if (draft.body !== undefined) setBody(draft.body);
      if (draft.published !== undefined) setPublished(draft.published);
    } catch {
      // corrupt draft — ignore
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load existing post — runs on first auth per slug; skips reload on re-auth
  useEffect(() => {
    if (authState !== 'authenticated') return;
    if (!slug) {
      setLoading(false);
      return;
    }
    if (loadedSlugRef.current === slug) return;
    loadedSlugRef.current = slug;
    if (!BASE_URL) {
      setError('API not configured');
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(sectionUrl('writing', slug), {
          headers: { Authorization: `Bearer ${googleToken!}` },
        });
        if (res.status === 401) {
          signOut();
          return;
        }
        if (!res.ok) throw new Error(`${res.status}`);
        const json = await res.json();
        const post = json.post ?? json;
        setTitle(post.title ?? '');
        setDescription(post.description ?? '');
        setBody(post.body ?? '');
        setPublished(post.published ?? false);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, authState]); // eslint-disable-line react-hooks/exhaustive-deps

  // Local-storage autosave — only when authenticated
  useEffect(() => {
    if (authState !== 'authenticated') return;
    const lsKey = slug ? `write-draft-${slug}` : 'write-new-draft';

    // localStorage every 2s
    localTimerRef.current = setInterval(() => {
      localStorage.setItem(lsKey, JSON.stringify({ ...latestDraft.current, savedAt: Date.now() }));
    }, 2000);

    return () => {
      if (localTimerRef.current) clearInterval(localTimerRef.current);
    };
  }, [slug, authState]); // eslint-disable-line react-hooks/exhaustive-deps

  const blocker = useBlocker(unsavedSinceApi);

  useBeforeUnload(useCallback((e: BeforeUnloadEvent) => {
    if (unsavedSinceApi) e.preventDefault();
  }, [unsavedSinceApi]));

  const handleSave = async () => {
    if (!BASE_URL) {
      setError('API not configured');
      return;
    }
    setSaving(true);
    setError(null);
    setAutosaveStatus('Saving…');
    try {
      const token = googleToken;
      if (!token) throw new Error('Not signed in');
      if (!slug) {
        // New post: POST
        const resp = await fetch(sectionUrl('writing'), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ title, description, body, published }),
        });
        if (resp.status === 401) {
          signOut();
          setAutosaveStatus('Unsaved changes');
          return;
        }
        if (!resp.ok) throw new Error(`Save failed: ${resp.status}`);
        const { slug: newSlug } = await resp.json();
        localStorage.removeItem('write-new-draft');
        setUnsavedSinceApi(false);
        navigate(`/write/${newSlug}`, { replace: true });
      } else {
        // Existing post: PUT
        const resp = await fetch(sectionUrl('writing', slug), {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ title, description, body, published }),
        });
        if (resp.status === 401) {
          signOut();
          setAutosaveStatus('Unsaved changes');
          return;
        }
        if (!resp.ok) throw new Error(`Save failed: ${resp.status}`);
        const lsKey = `write-draft-${slug}`;
        localStorage.setItem(lsKey, JSON.stringify({ title, description, body, published, savedAt: Date.now() }));
      }
      setUnsavedSinceApi(false);
      setAutosaveStatus('Saved');
      setTimeout(() => setAutosaveStatus(''), 3000);
    } catch (e: any) {
      setError('Save failed. Your work is preserved locally — try again.');
      setAutosaveStatus('Unsaved changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!slug) return;
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    if (!BASE_URL) { setError('API not configured'); return; }
    try {
      const token = googleToken;
      if (!token) throw new Error('Not signed in');
      const resp = await fetch(sectionUrl('writing', slug), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      // CRITICAL: never call resp.json() on DELETE — 204 has no body
      if (resp.ok || resp.status === 204) {
        localStorage.removeItem(`write-draft-${slug}`);
        navigate('/write');
      } else {
        setError(`Delete failed: ${resp.status}`);
      }
    } catch {
      setError('Delete failed. Try again.');
    }
  };

  // Auth gate — AFTER all hooks, BEFORE render
  if (authState === 'loading') {
    return (
      <div className="editor-page">
        <NavBar />
        <div className="editor-content"><WritingCursor /></div>
      </div>
    );
  }

  if (authState === 'unauthenticated') {
    return (
      <div className="editor-page">
        <NavBar />
        <div className="editor-login-view">
          <h1>Sign in to write</h1>
          <p>This area is private. Sign in with your Google account to access the editor.</p>
          <div ref={googleBtnRef} />
        </div>
      </div>
    );
  }

  return (
    <div className="editor-page">
      <NavBar />
      <div className="editor-content">
        {error && <p className="editor-error">{error}</p>}
        {loading && <WritingCursor />}
        {!loading && (
          <>
            {blocker.state === 'blocked' && (
              <div className="editor-unsaved-modal-backdrop">
                <div className="editor-unsaved-modal">
                  <h2>Leave without saving?</h2>
                  <p>Your changes will be lost. They&rsquo;re saved locally, but not published.</p>
                  <div className="editor-unsaved-modal-actions">
                    <button className="editor-unsaved-modal-leave" onClick={() => blocker.proceed?.()}>Leave</button>
                    <button className="editor-unsaved-modal-stay" onClick={() => blocker.reset?.()}>Stay here</button>
                  </div>
                </div>
              </div>
            )}
            <div className="editor-toolbar">
              <a href="/write" className="editor-back">
                &larr; All posts
              </a>
              <span className="editor-autosave">{autosaveStatus}</span>
              <div className="editor-actions">
                <button
                  className="editor-save-btn"
                  disabled={saving}
                  onClick={handleSave}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <label className="editor-publish-group">
                  <input
                    type="checkbox"
                    checked={published}
                    onChange={e => {
                      setPublished(e.target.checked);
                      setUnsavedSinceApi(true);
                      setAutosaveStatus('Unsaved changes');
                    }}
                  />
                  <span>Published</span>
                </label>
                {slug && (
                  <button className="editor-delete-btn" onClick={handleDelete}>
                    Delete Post
                  </button>
                )}
              </div>
            </div>
            <hr className="editor-divider" />
            <input
              type="text"
              className="editor-field-title"
              placeholder="Title"
              value={title}
              onChange={e => {
                setTitle(e.target.value);
                setUnsavedSinceApi(true);
                setAutosaveStatus('Unsaved changes');
              }}
            />
            <input
              type="text"
              className="editor-field-desc"
              placeholder="Description"
              value={description}
              onChange={e => {
                setDescription(e.target.value);
                setUnsavedSinceApi(true);
                setAutosaveStatus('Unsaved changes');
              }}
            />
            <hr className="editor-divider" />
            <textarea
              className="editor-field-body"
              placeholder="Write in markdown…"
              value={body}
              rows={12}
              onChange={e => {
                setBody(e.target.value);
                setUnsavedSinceApi(true);
                setAutosaveStatus('Unsaved changes');
              }}
            />
            {slug && <VersionHistoryPanel client={writeVersionClient} />}
          </>
        )}
      </div>
    </div>
  );
}
